import { ScreeningReportsResponse } from "../entities/ScreeningReports";
import {
  ScreeningStatus,
  ScreeningSource,
  ScreeningRating,
  ScreeningReport,
} from "../entities/CustomerAnalysis";
import { normalizeScreeningStatus } from "../entities/ScreeningStatusNormalizer";

// API response structure for screening reports history endpoint
export interface ApiScreeningReportsResponse {
  code: string;
  message: string;
  data: ApiScreeningReport[];
}

export interface ApiScreeningReport {
  title: string;
  dataset: string;
  status: "CLEAR" | string;
  rating: "LOW_RISK" | "MEDIUM_RISK" | "HIGH_RISK" | "CRITICAL_RISK" | string;
  date: string;
  businessKey: string;
  lsegId: string;
  filename?: string;
}

export class ScreeningReportsMapper {
  static toDomainModel(
    apiResponse: ApiScreeningReportsResponse,
  ): ScreeningReportsResponse {
    return {
      code: apiResponse.code,
      message: apiResponse.message,
      data: apiResponse.data.map((item) => this.toDomainItem(item)),
    };
  }

  static toDomainItem(apiItem: ApiScreeningReport): ScreeningReport {
    return {
      title: apiItem.title,
      client: this.extractClientFromTitle(apiItem.title), // Derive from title
      dataset: apiItem.dataset,
      status: this.mapStatus(apiItem.status),
      source: this.deriveSource(apiItem.lsegId, apiItem.dataset), // Derive from lsegId or dataset
      date: apiItem.date,
      rating: apiItem.rating as ScreeningRating,
      businessKey: apiItem.businessKey,
      lsegId: apiItem.lsegId,
      filename: apiItem.filename,
    };
  }

  /**
   * Maps API status to domain ScreeningStatus
   */
  private static mapStatus(status: string): ScreeningStatus {
    return normalizeScreeningStatus(status);
  }

  /**
   * Derives source from lsegId or dataset
   */
  private static deriveSource(
    lsegId?: string,
    dataset?: string,
  ): ScreeningSource {
    if (lsegId) {
      return "LSEG World-Check";
    }
    if (
      dataset?.toLowerCase().includes("internal") ||
      dataset?.toLowerCase().includes("dossier")
    ) {
      return "Internal Dossier";
    }
    return "External Provider";
  }

  /**
   * Extracts client name from title (e.g., "Screening LSEG - Jean DUPONT" -> "Jean DUPONT")
   */
  private static extractClientFromTitle(title: string): string {
    // Try to extract name after dash or colon
    const match = title.match(/- (.+)$|: (.+)$/);
    if (match) {
      return match[1] || match[2] || "";
    }
    return "";
  }
}
